import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NFTCard, NFTCardSkeleton } from "@/components/marketplace/NFTCard";
import { MarketplaceNFT, CollectionInfo } from "@/hooks/useMarketplaceNFTs";
import { cn } from "@/lib/utils";

interface CollectionCarouselProps {
  collection: CollectionInfo;
  nfts: MarketplaceNFT[];
  loading?: boolean;
  className?: string;
}

export function CollectionCarousel({ collection, nfts, loading, className }: CollectionCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  if (loading) {
    return (
      <Card className={cn("bg-card/80 backdrop-blur-sm border border-border/50 shadow-soft", className)}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-muted animate-pulse" />
              <div className="space-y-1">
                <div className="h-3 w-28 bg-muted rounded animate-pulse" />
                <div className="h-3 w-40 bg-muted rounded animate-pulse" />
              </div>
            </div>
            <div className="h-6 w-20 bg-muted rounded animate-pulse" />
          </div>
        </CardHeader>
        <CardContent className="pb-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <NFTCardSkeleton key={i} />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (nfts.length === 0) {
    return null;
  }

  const showNavigation = nfts.length > 4;

  return (
    <Card className={cn("bg-card/80 backdrop-blur-sm border border-border/50 shadow-soft", className)}>
      <CardHeader className="py-3">
        <CardTitle className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <FolderOpen className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-primary text-sm truncate">{collection.collection_name}</h4>
              <p className="text-xs text-muted-foreground">
                {collection.nft_count} NFTs • {collection.total_sales} sales
              </p>
            </div>
          </div>
          <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary text-xs">
            Collection
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="pb-3">
        {showNavigation ? (
          <div className="relative group">
            <div ref={emblaRef} className="overflow-hidden">
              <div className="flex gap-3">
                {nfts.map((nft) => (
                  <div
                    key={nft.id}
                    className="flex-shrink-0 w-[calc(50%-6px)] sm:w-[calc(33.333%-8px)] lg:w-[calc(25%-9px)]"
                  >
                    <NFTCard nft={nft} showTrending={false} />
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop Navigation Arrows */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10",
                "h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg",
                "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                 "hover:bg-primary/10 disabled:opacity-0"
               )}
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-5 w-5 text-primary" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10",
                "h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg",
                "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                 "hover:bg-primary/10 disabled:opacity-0"
               )}
              onClick={scrollNext}
              disabled={!canScrollNext}
              aria-label="Next page"
            >
              <ChevronRight className="h-5 w-5 text-primary" />
            </Button>

            {/* Dot Indicators */}
            {scrollSnaps.length > 1 && (
              <div className="flex justify-center gap-1.5 pt-2" aria-label="Carousel pagination">
                {scrollSnaps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollTo(index)}
                    aria-label={`Go to page ${index + 1}`}
                     className={cn(
                       "w-2 h-2 rounded-full transition-all duration-200",
                       index === selectedIndex ? "bg-primary w-4" : "bg-primary/30 hover:bg-primary/50"
                     )}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {nfts.map((nft) => (
              <NFTCard key={nft.id} nft={nft} showTrending={false} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
